import {motion} from 'framer-motion';
import {type RefObject, useEffect, useRef, useState} from 'react';
import {FaArrowAltCircleDown, FaArrowRight} from 'react-icons/fa';
import {Link} from 'react-router-dom';
import styled, {css, keyframes} from 'styled-components';
// The main webapp's landing footer (CIP links + the Stanford global footer with
// all its legal links). Reused verbatim rather than duplicated — PAI mounts the
// full CIP app shell, so its TranslationContext, router, and `landing.scss`
// styles are already in scope here.
import {Footer as SiteFooter} from 'landing/components/Footer';
import {StanfordLogo} from 'branding/StanfordLogo';
import {useAuthUid} from 'hooks/user/useUserId';
import {VisuallyHidden} from '../a11y/VisuallyHidden';
import {HeroTitleMorph} from '../hero/HeroTitleMorph';
import {PiDotPop} from '../hero/PiDotPop';
import {COURSE_INFO_ID, CourseInfo} from './CourseInfo';

// ── Keyframes ─────────────────────────────────────────────────────────────

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const bgSettle = keyframes`
  from { transform: scale(1.08); }
  to   { transform: scale(1); }
`;

const fadeBlurUp = keyframes`
  from { opacity: 0; transform: translateY(16px); filter: blur(8px); }
  to   { opacity: 1; transform: translateY(0);    filter: blur(0px); }
`;

// Same fade as fadeBlurUp, but keeps HeroFooter's translateX(-50%) centering
// in the transform through every keyframe — a plain fadeBlurUp would replace
// that static transform for the animation's duration and un-center the button.
const footerFadeBlurUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(16px); filter: blur(8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0);    filter: blur(0px); }
`;

// The campus photo fades away just as the PAI→title morph begins (the morph's
// own start is gated by HeroTitleMorph's startDelayMs, kept in sync below), so
// the hero settles into its dark backing color as the title takes over.
const bgFadeToBlack = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

/**
 * The apply cards' fill — the same Stanford cardinal the old "Express
 * Interest" button used.
 */
const HERO_CARDINAL = '#8c1515';

/**
 * The π-dot finale's red: the cardinal family (see {@link HERO_CARDINAL})
 * warmed toward salmon — hue pulled a few degrees orange and lightened — so
 * the small π strokes glow against the hero's black backing without reading
 * as full salmon.
 */
const HERO_PI_RED = '#db6b5c';

/**
 * Single shared reveal delay, in seconds, for everything below the title:
 * the CTA buttons fade in at this point while the title morph is still
 * playing — nothing queues behind the full morph sequence.
 */
const HERO_COPY_DELAY_SEC = 1.2;

/** Duration of the CTA row's own fade-in (see {@link HeroCta}). */
const HERO_CTA_FADE_SEC = 0.8;

/**
 * The full settled title as one plain string. The morphing title is rendered
 * as per-character glyph spans and is hidden from assistive technology (see
 * the aria-hidden wrapper in the JSX); screen readers get this text instead,
 * and the hero section is labelled by it via {@link HERO_TITLE_ID}.
 */
const HERO_TITLE_TEXT = 'Probability for Artificial Intelligence';

/** DOM id of the screen-reader title, referenced by the hero's aria-labelledby. */
const HERO_TITLE_ID = 'pai-hero-title';

/**
 * When the below-title copy has fully revealed, in ms from mount: the shared
 * reveal start plus the CTA row's own fade-in duration. The π-dot finale
 * waits for this — it's the hero's closing beat, after everything else is on
 * screen.
 */
const HERO_COPY_REVEALED_MS = Math.round(
  (HERO_COPY_DELAY_SEC + HERO_CTA_FADE_SEC) * 1000,
);

// ── Splash wrapper ─────────────────────────────────────────────────────────

const Splash = styled.div`
  /* Matches the main webapp's default body font (see style.scss), so this
     page reads as part of the same product outside the hero's own Jost
     title treatment. */
  font-family: 'Nunito', Helvetica, sans-serif;
  -webkit-font-smoothing: antialiased;
  color: #1a1a1a;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
`;

// ── Hero ───────────────────────────────────────────────────────────────────

const Hero = styled.section`
  position: relative;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* Backing color the photo fades into — pure black so the settled hero reads
     as a deep black once the photo is gone. */
  background: #000000;
`;

const HeroBg = styled.div`
  position: absolute;
  inset: 0;
  /* WebP recompress of the original campus JPEG (~290 KB vs ~1.8 MB). As a
     CSS background it's only discovered after the JS bundle executes, so
     index.html also preloads it on the landing route — keep both URLs in
     sync. */
  background-image: url('/pai-hero-campus.webp');
  background-size: cover;
  background-position: center 30%;
  transform: scale(1.04);
  animation:
    ${bgSettle} 1.6s ease forwards,
    /* Starts at 0.8s to line up with HeroTitleMorph's startDelayMs (800ms). */
      ${bgFadeToBlack} 1.6s ease 0.8s forwards;

  /* Reduced motion: jump straight to the hero's settled state — the photo
     fully faded into the black backing — matching HeroTitleMorph, which
     skips directly to the settled title. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: scale(1);
    opacity: 0;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    160deg,
    rgba(2, 2, 5, 0.95) 0%,
    rgba(8, 4, 6, 0.92) 55%,
    rgba(40, 6, 6, 0.82) 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  /* Wider than the other hero copy strictly needs, so the title has extra
     room to stay on fewer lines on larger screens. */
  max-width: 960px;
  /* Extra bottom padding clears the absolutely-positioned HeroFooter link so
     it never overlaps the apply cards on short viewports. */
  padding: 2rem 2.5rem 4.5rem;
  text-align: center;

  /* Trim the side padding on narrow phones so the larger minimum title size
     (see TitleStack's clamp floor) still leaves room for the longest single
     title word without overflowing. */
  @media (max-width: 480px) {
    padding: 2rem 1.25rem 4.5rem;
  }
`;

// ── Animated title ─────────────────────────────────────────────────────────
// "PAI" holds for a beat, then glyph-morphs into "Probability / for / AI"
// (see hero/HeroTitleMorph.tsx) — letters glide from their "PAI" position to
// their landing spot in the full title, rather than a text swap or width
// reveal.

const TitleStack = styled.h1`
  /* Positioned ancestor for PiDotPop's absolutely-positioned pop layer. */
  position: relative;
  font-family: 'Jost', system-ui, sans-serif;
  /* Floor raised from 2rem so the title stays legible on phones/iPad; each
     title word sits on its own line (see targetLines below), so even the
     longest word fits at this minimum within HeroContent's mobile padding. */
  font-size: clamp(2.5rem, 8vw, 5.5rem);
  font-weight: 700;
  line-height: 1.1;
  color: #ffffff;
  margin-bottom: 1.5rem;
  animation: ${fadeBlurUp} 0.7s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const HeroDeadline = styled.p`
  width: 100%;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 1rem;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

// The entrance fade only plays on the first run: replays (the π-click
// restart remounts the hero subtree) keep the buttons on screen rather than
// making the already-seen CTAs vanish and fade back in.
const HeroCta = styled.div<{$animateEntrance: boolean}>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  ${({$animateEntrance}) =>
    $animateEntrance &&
    css`
      animation: ${fadeBlurUp} ${HERO_CTA_FADE_SEC}s ease
        ${HERO_COPY_DELAY_SEC}s both;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

// Routes for PAI's own applications. Hardcoded rather than derived — matches
// the same pattern in ../PaiCourseHomePage.tsx, since PAI only has one active
// cohort right now.
const PAI_STUDENT_APPLY_ROUTE = '/apply/pai1/student';
const PAI_SL_APPLY_ROUTE = '/apply/pai1/sl';
// PaiCourseHomePage routes each signed-in visitor on from here: members to
// their role's home, everyone else to their application.
const PAI_COURSE_HOME_ROUTE = '/pai1';

const ApplyCardRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  width: 100%;
`;

const ApplyCardTitle = styled.span`
  display: block;
  font-size: 1.05rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.4rem;
`;

const ApplyCardBody = styled.span`
  display: block;
  font-size: 0.9rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
`;

const ApplyCard = styled(Link)`
  flex: 1 1 220px;
  max-width: 280px;
  padding: 0.85rem 1.25rem;
  border: none;
  border-radius: 12px;
  background: ${HERO_CARDINAL};
  text-align: center;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(140, 21, 21, 0.45);
  transition:
    background 0.2s,
    transform 0.15s,
    box-shadow 0.2s;

  &:hover,
  &:focus-visible {
    background: #a01818;
    transform: translateY(-2px);
    box-shadow: 0 6px 28px rgba(140, 21, 21, 0.55);
  }

  /* Explicit keyboard-focus ring: the default UA outline can hug the card
     invisibly against the dark hero, so draw a clear offset ring. */
  &:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }
`;

// Moved out of the HeroCta row into its own strip along the bottom edge of
// the hero: a quieter, secondary way to keep browsing, rather than a button
// competing with the two apply cards above it. Still fades in alongside the
// HeroCta row (same $animateEntrance gating, see HeroCta) so all three CTAs
// arrive together.
const HeroFooter = styled.button<{$animateEntrance: boolean}>`
  position: absolute;
  left: 50%;
  bottom: 1.75rem;
  transform: translateX(-50%);
  /* Painted above HeroContent (z-index: 1) — otherwise HeroContent's own box
     (including its blank bottom padding, added to clear this footer) sits on
     top in paint order and swallows clicks on this button despite being
     visually empty there. */
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1.25rem;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: #fff;
  color: ${HERO_CARDINAL};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  transition:
    background 0.2s,
    transform 0.15s,
    box-shadow 0.2s;

  &:hover {
    background: #f0f0f0;
    transform: translateX(-50%) translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }

  /* Matches the focus ring used elsewhere in the hero. */
  &:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  ${({$animateEntrance}) =>
    $animateEntrance &&
    css`
      opacity: 0;
      animation: ${footerFadeBlurUp} ${HERO_CTA_FADE_SEC}s ease
        ${HERO_COPY_DELAY_SEC}s both;

      @media (prefers-reduced-motion: reduce) {
        opacity: 1;
        animation: none;
      }
    `}
`;

// Shown to signed-in visitors only, with no entrance delay: returning
// students come here often and shouldn't wait out the intro to get to class.
// The short fade only covers the auth restore on a hard reload; like the
// other CTAs it stays put on replays (see HeroCta).
const GoToClassPill = styled(Link)<{$animateEntrance: boolean}>`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  /* Above HeroContent (z-index: 1), same as HeroFooter. */
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: none;
  background: #fff;
  color: ${HERO_CARDINAL};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  ${({$animateEntrance}) =>
    $animateEntrance &&
    css`
      animation: ${fadeIn} 0.3s ease both;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
  transition:
    background 0.2s,
    transform 0.15s,
    box-shadow 0.2s;

  &:hover,
  &:focus-visible {
    background: #f0f0f0;
    color: ${HERO_CARDINAL};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }

  /* Matches the focus ring used elsewhere in the hero. */
  &:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  @media (max-width: 480px) {
    top: 1rem;
    right: 1rem;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
`;

// Same seal/corner convention as the slide master (see
// createMasterLogoElement in slides/schema/deck.ts): the seal's transparent
// background reads fine on both light and dark backgrounds, so no separate
// light variant is needed here.
const HeroStanfordSeal = styled(StanfordLogo)`
  position: absolute;
  right: 1.5rem;
  bottom: 1.5rem;
  width: 100px;
  height: 100px;

  @media (max-width: 480px) {
    right: 1rem;
    bottom: 1rem;
    width: 64px;
    height: 64px;
  }
`;

// ── Baked-in markup strokes ─────────────────────────────────────────────────
// Two pen strokes drawn with the PAI splash markup tool (?markup — see
// src/apps/pai/src/splash/markup/SplashMarkup.tsx) and pulled from its
// autosave doc (course/pai1/info/splashMarkup, desktop layer), baked in here
// as real SVG rather than left as an annotation overlay. Point coordinates
// are recorded as "page units" in SplashMarkup's own convention: 1/1000ths
// of the hero's rendered width, on BOTH axes (so a live SplashMarkup overlay
// — which spans the whole scrollable page — keeps its aspect ratio as that
// page reflows).
//
// This component deliberately does NOT reuse that convention for y: Hero's
// height is pinned to the viewport (`min-height: 100dvh`), decoupled from
// its width, so scaling y by width here would make the strokes drift toward
// the top as the window narrows with no corresponding height change (a real
// bug reported against an earlier version of this file). Instead, x scales
// by the hero's rendered width (HERO_MARKUP_UNITS) and y scales by its
// rendered height (HERO_MARKUP_REFERENCE_HEIGHT_UNITS) — see pxPerUnitX/Y in
// HeroMarkupOverlay. The two axes' scale factors intentionally diverge as
// the hero's aspect ratio departs from the reference one below, which very
// slightly distorts the strokes' shape — an acceptable trade for a stable
// vertical position, since these are decorative squiggles, not precise art.
const HERO_MARKUP_UNITS = 1000;

// Calibrated, not measured: chosen so pxPerUnitY approximately matches the
// old width-only pxPerUnit at a typical laptop viewport (~1500px wide,
// ~900px tall hero) — the size the strokes were last confirmed to look
// right at. Retune this if that no longer holds.
const HERO_MARKUP_REFERENCE_HEIGHT_UNITS = 600;

// The markup's natural (unshifted) leftmost extent is kept at least this far
// right of the title's actual rendered right edge — measured live (see
// titleRightPx in HeroMarkupOverlay), not calibrated like the constant
// above, since the title's own width varies with viewport size in ways the
// recording can't predict. Whenever the natural position would be closer
// than this (or overlapping), the whole overlay shifts right to restore the
// gap, rather than let it collide with the title it's pointing at.
const HERO_MARKUP_MIN_GAP_FROM_TITLE_PX = 0;

const HERO_MARKUP_STROKES: ReadonlyArray<{
  id: string;
  color: string;
  // A literal CSS pixel width — unlike x/y, NOT a page unit, and not scaled
  // by pxPerUnitX/Y — so the stroke reads with the same weight at any
  // viewport size, rather than getting visibly thinner as the hero narrows.
  width: number;
  points: ReadonlyArray<readonly [number, number]>;
}> = [
  {
    id: 'm2-5itlxx',
    color: HERO_PI_RED,
    width: 8,
    points: [
      [795.86, 237.87],
      [795.86, 234.98],
      [795.86, 232.83],
      [795.97, 230.3],
      [796.1, 227.56],
      [796.1, 224.8],
      [796.1, 222.58],
      [796.1, 219.56],
      [796.1, 217.42],
      [795.63, 214.56],
      [794.93, 212.21],
      [794.14, 209.48],
      [793.21, 207.46],
      [792.24, 205.4],
      [791.27, 203.22],
      [789.72, 200.61],
      [788.23, 198.25],
      [787.03, 196.34],
      [785.83, 194.63],
      [784.64, 192.95],
      [783.31, 191.25],
      [781.84, 189.53],
      [780.25, 187.69],
      [778.24, 185.78],
      [776.06, 184.08],
      [774.08, 182.74],
      [771.97, 181.6],
      [769.46, 180.79],
      [766.6, 180.14],
      [764.62, 179.71],
      [762.14, 179.21],
      [759.43, 178.9],
      [756.67, 178.51],
      [753.74, 178.23],
      [751.14, 178.21],
      [748.65, 178.21],
      [745.31, 178.21],
      [742.8, 178.21],
      [740.05, 178.21],
      [737.62, 178.21],
      [735.36, 178.41],
      [733.08, 178.68],
      [730.82, 178.98],
      [728.4, 179.4],
      [726.03, 179.92],
      [723.9, 180.68],
      [721.5, 181.27],
      [719.39, 182.26],
      [717.57, 183.66],
      [715.6, 184.45],
    ],
  },
  {
    id: 'm3-k07vbf',
    color: HERO_PI_RED,
    width: 8,
    points: [
      [732.76, 168.63],
      [730, 169.41],
      [727.43, 171.14],
      [724.35, 173.14],
      [722.55, 174.6],
      [719.92, 176.6],
      [717.84, 178.55],
      [716.13, 180.45],
      [714.38, 182.21],
      [712.49, 183.86],
      [711.26, 185.49],
      [713.44, 186.95],
      [715.96, 189.08],
      [717.56, 190.69],
      [719.77, 193.2],
      [722.13, 196.22],
      [724.8, 200.18],
      [725.72, 202.03],
      [726.71, 204.43],
    ],
  },
];

/**
 * Scales a polyline's page-unit points to pixels and builds the resulting
 * SVG path string. Takes independent x/y scale factors — see
 * {@link HERO_MARKUP_REFERENCE_HEIGHT_UNITS} for why y isn't scaled by the
 * same factor as x.
 */
function heroMarkupStrokePath(
  points: ReadonlyArray<readonly [number, number]>,
  pxPerUnitX: number,
  pxPerUnitY: number,
  shiftXPx = 0,
): string {
  if (points.length === 0) {
    return '';
  }
  const scaled: Array<[number, number]> = points.map(([x, y]) => [
    x * pxPerUnitX + shiftXPx,
    y * pxPerUnitY,
  ]);
  const [first, ...rest] = scaled;
  return (
    `M ${first[0]} ${first[1]}` + rest.map(([x, y]) => ` L ${x} ${y}`).join('')
  );
}

// Pen-draw timing, matching the slide editor's freehand "write" animation
// (see PEN_DRAW_SPEED_PX_PER_SEC/PEN_DRAW_MIN_SEC/PEN_DRAW_MAX_SEC in
// src/apps/webapp/src/slides/shared/enterDuration.ts): a constant on-screen
// pen speed, clamped so very short or very long strokes still read as a
// deliberate stroke rather than instant or sluggish.
const HERO_MARKUP_PEN_SPEED_PX_PER_SEC = 1600;
const HERO_MARKUP_PEN_DRAW_MIN_SEC = 0.4;
const HERO_MARKUP_PEN_DRAW_MAX_SEC = 2.5;

/** Total length of a polyline that's already in pixel space. */
function heroMarkupPolylineLengthPx(
  points: ReadonlyArray<readonly [number, number]>,
): number {
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i - 1];
    const [x2, y2] = points[i];
    length += Math.hypot(x2 - x1, y2 - y1);
  }
  return length;
}

/** A stroke's draw-on duration for a given on-screen pixel length. */
function heroMarkupPenDrawDurationSec(lengthPx: number): number {
  const raw = lengthPx / HERO_MARKUP_PEN_SPEED_PX_PER_SEC;
  return Math.min(
    HERO_MARKUP_PEN_DRAW_MAX_SEC,
    Math.max(HERO_MARKUP_PEN_DRAW_MIN_SEC, raw),
  );
}

const HeroMarkupSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  /* Decorative — never intercepts hero clicks. */
  pointer-events: none;
`;

// The text box drawn alongside HERO_MARKUP_STROKES, from the same markup
// autosave doc. Position/size are in the same page-unit space as the
// strokes; SplashMarkup itself converts unit -&gt; px via `pxPerUnit = pageWidth
// / UNITS` rather than an SVG viewBox (text boxes are plain HTML, not SVG),
// so this component does the same conversion below.
const HERO_MARKUP_TEXT = {
  id: 'm4-s41mcz',
  color: HERO_PI_RED,
  size: 16,
  x: 711,
  y: 249.7,
  w: 206.79,
  h: 54.99,
  text: 'A free Stanford course, taught with hundreds of teachers',
};

// Nudges the text left of its recorded x, toward the arrow strokes it
// follows — a fixed 20px at wide viewports, growing linearly to 60px (20 +
// 40 extra) as the viewport narrows from HERO_MARKUP_TEXT_NUDGE_WIDE_PX down
// to HERO_MARKUP_TEXT_NUDGE_NARROW_PX, so the two sit closer together on
// smaller screens rather than holding a constant gap. See
// heroMarkupTextLeftNudgePx (below), which computes the actual interpolated
// value for the current viewport width. Only the text moves here — the
// arrow strokes' own position is untouched.
const HERO_MARKUP_TEXT_LEFT_NUDGE_PX = -20;
const HERO_MARKUP_TEXT_EXTRA_NUDGE_MAX_PX = 40;
const HERO_MARKUP_TEXT_NUDGE_WIDE_PX = 1600;
// Matches HeroMarkupGroup's hide cutoff — the extra nudge maxes out exactly
// where the whole overlay is about to disappear anyway.
const HERO_MARKUP_TEXT_NUDGE_NARROW_PX = 1000;

/**
 * The text's left nudge (see {@link HERO_MARKUP_TEXT_LEFT_NUDGE_PX}) for a
 * given hero width: a constant -5px at or above
 * {@link HERO_MARKUP_TEXT_NUDGE_WIDE_PX}, growing linearly (more negative,
 * i.e. further left) down to -45px at or below
 * {@link HERO_MARKUP_TEXT_NUDGE_NARROW_PX}.
 */
function heroMarkupTextLeftNudgePx(heroWidthPx: number): number {
  const range =
    HERO_MARKUP_TEXT_NUDGE_WIDE_PX - HERO_MARKUP_TEXT_NUDGE_NARROW_PX;
  const narrowProgress = Math.min(
    1,
    Math.max(0, (HERO_MARKUP_TEXT_NUDGE_WIDE_PX - heroWidthPx) / range),
  );
  return (
    HERO_MARKUP_TEXT_LEFT_NUDGE_PX -
    narrowProgress * HERO_MARKUP_TEXT_EXTRA_NUDGE_MAX_PX
  );
}

// Floor on the text's own rendered size (see textPxPerUnit in
// HeroMarkupOverlay): below this, page-unit scaling would shrink the callout
// past legibility as the viewport narrows toward the tablet cutoff where
// HeroMarkupGroup hides the whole overlay outright.
const HERO_MARKUP_TEXT_MIN_FONT_PX = 16;

const HeroMarkupTextFrame = styled.div`
  position: absolute;
  /* Decorative — never intercepts hero clicks. */
  pointer-events: none;
`;

// Diverges from SplashMarkup's own TextItemBox (KaTeX_Main) — a chalkboard
// face reads better for a hand-drawn callout. System font, not a loaded
// webfont: 'Chalkboard SE' is built into macOS/iOS, falling back to Comic
// Sans MS/cursive elsewhere rather than pulling in a new font file.
const HeroMarkupTextBox = styled.div`
  white-space: pre-wrap;
  font-family: 'Chalkboard SE', 'Chalkboard', 'Comic Sans MS', cursive;
  line-height: 1.25;
  text-align: center;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.25);
`;

// Matches the slide editor's text "write" reveal (see runWriteReveal in
// src/apps/webapp/src/slides/shared/writeReveal.ts and its timing constants
// in enterDuration.ts): each non-space character fades in individually,
// staggered, rather than the whole box fading in at once.
const HERO_MARKUP_TEXT_CHAR_STAGGER_SEC = 0.035;
const HERO_MARKUP_TEXT_MAX_WRITE_SPAN_SEC = 1.8;
const HERO_MARKUP_TEXT_CHAR_FADE_SEC = 0.18;

/**
 * Reveals `text` one character at a time via the Web Animations API,
 * mirroring the slide editor's text "write" animation. Space characters stay
 * plain text (always visible, no stagger beat spent on them); every other
 * character starts hidden and is queued to fade in once `play` turns true.
 *
 * @param props - The component props.
 * @param props.text - The text to reveal. Assumed single-line (no
 *   newlines) — matches {@link HERO_MARKUP_TEXT}.
 * @param props.play - Starts the reveal once `true`. Ignored on subsequent
 *   changes: the reveal runs at most once per mount.
 * @param props.delaySec - Extra delay, in seconds, before the first
 *   character starts revealing (e.g. so the text writes in only after the
 *   pen strokes above it have finished drawing).
 * @return The text, split into per-character spans.
 */
function HeroMarkupTextReveal({
  text,
  play,
  delaySec,
}: {
  text: string;
  play: boolean;
  delaySec: number;
}) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const hasPlayedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!play || hasPlayedRef.current || !container) {
      return;
    }
    hasPlayedRef.current = true;
    const chars = Array.from(
      container.querySelectorAll<HTMLElement>('[data-hero-markup-char]'),
    );
    const staggerSec = Math.min(
      HERO_MARKUP_TEXT_CHAR_STAGGER_SEC,
      HERO_MARKUP_TEXT_MAX_WRITE_SPAN_SEC / Math.max(chars.length, 1),
    );
    chars.forEach((el, index) => {
      el.animate([{opacity: 0}, {opacity: 1}], {
        duration: HERO_MARKUP_TEXT_CHAR_FADE_SEC * 1000,
        delay: (delaySec + index * staggerSec) * 1000,
        easing: 'ease-out',
        fill: 'both',
      });
    });
  }, [play, delaySec]);

  return (
    <span ref={containerRef}>
      {[...text].map((char, index) =>
        char === ' ' ? (
          ' '
        ) : (
          // Index as key is safe here: this static string never reorders,
          // gains, or loses characters — index-keyed rendering is only risky
          // when list contents can change.
          <span key={index} data-hero-markup-char style={{opacity: 0}}>
            {char}
          </span>
        ),
      )}
    </span>
  );
}

// Explicitly sized to Hero's own box (rather than left unpositioned) so
// HeroMarkupSvg/HeroMarkupTextFrame's `position: absolute` resolves against
// it predictably, matching how the rest of the hero's overlay layers work.
const HeroMarkupGroup = styled.div`
  position: absolute;
  inset: 0;

  /* HERO_MARKUP_TEXT's position/size were captured at one specific desktop
     viewport width. Page-unit scaling keeps it readable down to a point,
     but narrower than that it starts overlapping the title/subhead copy it
     was never laid out against — hide the whole overlay rather than let it
     collide. */
  @media (max-width: 999px) {
    display: none;
  }
`;

/**
 * Renders {@link HERO_MARKUP_STROKES} and {@link HERO_MARKUP_TEXT} scaled to
 * the hero's current size, mirroring SplashMarkup's own viewBox/pxPerUnit
 * math so the annotations keep their position and aspect ratio across
 * viewport widths. Stays hidden until `animate` turns on, then draws the
 * strokes and writes the text on (see {@link HeroMarkupTextReveal}).
 *
 * @param props - The component props.
 * @param props.animate - Whether to play the draw-on/write-on entrance. Pass
 *   `false` until the moment the annotations should first appear (e.g. once
 *   the π has finished popping in and settled — see {@link PiDotPop}'s
 *   `onSettled`), since the overlay itself mounts immediately with the rest
 *   of the hero.
 * @param props.titleRef - Ref to the settled title (see {@link TitleStack}).
 *   Measured live so the overlay can keep {@link
 *   HERO_MARKUP_MIN_GAP_FROM_TITLE_PX} of clearance from the title's actual
 *   rendered right edge, whatever that happens to be at the current
 *   viewport size.
 * @return The markup overlay (strokes + text box).
 */
function HeroMarkupOverlay({
  animate,
  titleRef,
}: {
  animate: boolean;
  titleRef: RefObject<HTMLElement | null>;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  // Starts at 1 (an arbitrary square aspect ratio), not 0: the annotations
  // stay unmounted until a size is known, so the SVG element measuring its
  // own size would never exist to observe in the first place — always render
  // it, and let the first ResizeObserver callback correct the aspect ratio.
  const [size, setSize] = useState({width: 0, height: 1});
  // The title's right edge, in px relative to the SVG's own left edge (the
  // same box as Hero, since the SVG is position: absolute; inset: 0 inside
  // it) — see HERO_MARKUP_MIN_GAP_FROM_TITLE_PX.
  const [titleRightPx, setTitleRightPx] = useState(0);

  useEffect(() => {
    const svgEl = svgRef.current;
    const titleEl = titleRef.current;
    if (!svgEl) {
      return;
    }
    const measureTitleRight = () => {
      if (titleEl) {
        setTitleRightPx(
          titleEl.getBoundingClientRect().right -
            svgEl.getBoundingClientRect().left,
        );
      }
    };
    const sizeObserver = new ResizeObserver(([entry]) => {
      setSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
      measureTitleRight();
    });
    sizeObserver.observe(svgEl);
    // The title can reflow independently of the hero's own box size (e.g. a
    // late-loading font), so it gets its own observer rather than relying
    // solely on sizeObserver's callback above.
    const titleObserver = titleEl
      ? new ResizeObserver(measureTitleRight)
      : null;
    titleObserver?.observe(titleEl);
    return () => {
      sizeObserver.disconnect();
      titleObserver?.disconnect();
    };
  }, [titleRef]);

  // x scales with the hero's rendered width; y scales with its rendered
  // height (see the module comment above HERO_MARKUP_REFERENCE_HEIGHT_UNITS
  // for why the two axes deliberately use different scale factors).
  const pxPerUnitX = size.width > 0 ? size.width / HERO_MARKUP_UNITS : 0;
  const pxPerUnitY =
    size.height > 0 ? size.height / HERO_MARKUP_REFERENCE_HEIGHT_UNITS : 0;
  // Floored separately from pxPerUnitX, and only for the text's own size
  // (not its left/top position): below this, page-unit scaling would shrink
  // the callout past legibility as the viewport narrows toward the tablet
  // cutoff where HeroMarkupGroup hides it outright. The box's top-left
  // corner stays anchored at the unfloored position (right by the arrow's
  // tip); only its width/height/font-size grow to fit the larger text.
  const textPxPerUnit = Math.max(
    pxPerUnitX,
    HERO_MARKUP_TEXT_MIN_FONT_PX / HERO_MARKUP_TEXT.size,
  );

  // Strokes draw one after another (not simultaneously) — like an actual
  // pen would — and the text starts writing only once both have finished.
  // Lengths are computed from the already px-scaled points (not raw page
  // units), since x/y no longer share one scale factor.
  const scaledStrokePoints = HERO_MARKUP_STROKES.map(stroke =>
    stroke.points.map(
      ([x, y]) => [x * pxPerUnitX, y * pxPerUnitY] as [number, number],
    ),
  );
  const strokeDurationsSec = scaledStrokePoints.map(points =>
    heroMarkupPenDrawDurationSec(heroMarkupPolylineLengthPx(points)),
  );
  let cumulativeDelaySec = 0;
  const strokeStartDelaysSec = strokeDurationsSec.map(durationSec => {
    const delaySec = cumulativeDelaySec;
    cumulativeDelaySec += durationSec;
    return delaySec;
  });
  const totalStrokeDurationSec = cumulativeDelaySec;

  const textLeftNudgePx = heroMarkupTextLeftNudgePx(size.width);

  // Keeps HERO_MARKUP_MIN_GAP_FROM_TITLE_PX of clearance from the title's
  // real rendered right edge: if the overlay's own unshifted leftmost point
  // (across both strokes and the text box) would sit closer than that — or
  // overlap it outright — the whole overlay shifts right by the difference.
  const naturalLeftmostPx = Math.min(
    ...scaledStrokePoints.flat().map(([x]) => x),
    HERO_MARKUP_TEXT.x * pxPerUnitX + textLeftNudgePx,
  );
  const requiredLeftPx = titleRightPx + HERO_MARKUP_MIN_GAP_FROM_TITLE_PX;
  const shiftXPx = Math.max(0, requiredLeftPx - naturalLeftmostPx);

  return (
    <HeroMarkupGroup>
      <HeroMarkupSvg
        ref={svgRef}
        // Exactly the hero's own pixel box — 1 SVG user unit = 1 CSS pixel —
        // since the path data below is already scaled to pixels with the
        // two independent per-axis factors. No further viewBox scaling.
        viewBox={`0 0 ${size.width} ${size.height}`}
        aria-hidden="true"
      >
        {size.width > 0 &&
          HERO_MARKUP_STROKES.map((stroke, index) => (
            <motion.path
              key={stroke.id}
              d={heroMarkupStrokePath(
                stroke.points,
                pxPerUnitX,
                pxPerUnitY,
                shiftXPx,
              )}
              stroke={stroke.color}
              strokeWidth={stroke.width}
              // Butt, not round: a round cap on a zero-length path (the
              // pathLength: 0 starting state below) still paints a small
              // filled dot at the start point, visible before the stroke
              // begins drawing. Butt caps paint nothing at zero length.
              strokeLinecap="butt"
              strokeLinejoin="round"
              fill="none"
              initial={{pathLength: 0}}
              animate={{pathLength: animate ? 1 : 0}}
              transition={{
                duration: strokeDurationsSec[index],
                ease: 'easeInOut',
                delay: strokeStartDelaysSec[index],
              }}
            />
          ))}
      </HeroMarkupSvg>
      {pxPerUnitX > 0 && (
        <HeroMarkupTextFrame
          style={{
            left: HERO_MARKUP_TEXT.x * pxPerUnitX + textLeftNudgePx + shiftXPx,
            top: HERO_MARKUP_TEXT.y * pxPerUnitY,
            width: HERO_MARKUP_TEXT.w * textPxPerUnit,
            height: HERO_MARKUP_TEXT.h * textPxPerUnit,
          }}
        >
          <HeroMarkupTextBox
            style={{
              fontSize: HERO_MARKUP_TEXT.size * textPxPerUnit,
              color: HERO_MARKUP_TEXT.color,
              width: '100%',
              height: '100%',
            }}
          >
            <HeroMarkupTextReveal
              text={HERO_MARKUP_TEXT.text}
              play={animate}
              delaySec={totalStrokeDurationSec}
            />
          </HeroMarkupTextBox>
        </HeroMarkupTextFrame>
      )}
    </HeroMarkupGroup>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

/**
 * The original PAI splash ("hero" version): a dark photographic hero with the
 * `HeroTitleMorph` PAI→title animation, followed by the shared interest form.
 * One of two looks selectable in {@link ../PaiSplashPage} (the other being the
 * Stanford-mirror version).
 *
 * @return The hero-style splash page.
 * @throws Never.
 */
export function HeroSplash() {
  const uid = useAuthUid();
  const titleRef = useRef<HTMLHeadingElement>(null);
  // The PiDotPop finale (the little π popping out of the "i" in
  // "Intelligence") is the hero's last beat: it needs the settled title's
  // glyphs to anchor to, AND it waits for the subheading/CTA reveal to
  // finish so it never competes with them for attention.
  const [titleSettled, setTitleSettled] = useState(false);
  const [copyRevealed, setCopyRevealed] = useState(false);
  // Gates HeroMarkupOverlay's entrance — the baked-in markup strokes/text
  // draw on only once the π has fully popped in and settled, so they don't
  // compete with the pop for attention.
  const [piSettled, setPiSettled] = useState(false);
  // Bumped by replayHero: keys the <Hero> subtree, so incrementing it
  // remounts every animated piece (photo settle/fade, title morph, write-on,
  // π pop) and the whole intro plays again from the top. The CTA buttons are
  // the one exception — their entrance fade is first-run-only (see HeroCta),
  // so on replays they stay on screen throughout.
  const [heroRunId, setHeroRunId] = useState(0);

  // Opt into Chrome-on-Android edge-to-edge while this hero is on screen (see
  // [#3615](https://github.com/CodeinPlace/cip/issues/3615)). Without
  // viewport-fit=cover, gesture-nav phones draw a light "chin" bar over the
  // gesture area, so the hero's 100dvh black stops short of the screen's
  // bottom edge. Scoped here (rather than index.html) so the rest of the PAI
  // app keeps the default viewport and its safe-area behavior; Chrome applies
  // viewport meta changes dynamically, and the cleanup restores the original.
  // https://developer.chrome.com/docs/css-ui/edge-to-edge
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!(meta instanceof HTMLMetaElement)) {
      return;
    }
    const originalContent = meta.content;
    meta.content = `${originalContent}, viewport-fit=cover`;
    return () => {
      meta.content = originalContent;
    };
  }, []);

  useEffect(() => {
    // Reduced motion: the CTA entrance renders fully visible immediately
    // (see the styled components' reduced-motion rules), so there is
    // nothing to wait for before the π finale.
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const timeoutId = window.setTimeout(
      () => {
        setCopyRevealed(true);
      },
      reducedMotion ? 0 : HERO_COPY_REVEALED_MS,
    );
    return () => {
      window.clearTimeout(timeoutId);
    };
    // Keyed to the run, not the mount: each replay needs the reveal timer to
    // start over alongside the remounted hero subtree.
  }, [heroRunId]);

  // Clicking the settled π (the hero's finale) restarts the whole sequence:
  // reset the two sequencing flags, then remount the keyed hero subtree.
  function replayHero() {
    setTitleSettled(false);
    setCopyRevealed(false);
    setPiSettled(false);
    setHeroRunId(runId => runId + 1);
  }

  function scrollToCourseInfo() {
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    document
      .getElementById(COURSE_INFO_ID)
      ?.scrollIntoView({behavior: reducedMotion ? 'auto' : 'smooth'});
  }

  return (
    <>
      {/* The main landmark holds the hero + course info; the shared footer
          stays a sibling so its own footer element keeps the contentinfo
          landmark role (a footer nested inside main loses it). */}
      <main>
        <Splash>
          <Hero key={heroRunId} aria-labelledby={HERO_TITLE_ID}>
            <HeroBg aria-hidden="true" />
            <HeroOverlay aria-hidden="true" />
            <HeroMarkupOverlay animate={piSettled} titleRef={titleRef} />

            {uid && (
              <GoToClassPill
                to={PAI_COURSE_HOME_ROUTE}
                $animateEntrance={heroRunId === 0}
              >
                Go to class
                <FaArrowRight aria-hidden="true" />
              </GoToClassPill>
            )}

            <HeroContent>
              <TitleStack ref={titleRef}>
                {/* Screen readers get the settled title as one plain string;
                  the morph below renders per-character glyph spans, which
                  assistive tech would announce as fragments. */}
                <VisuallyHidden id={HERO_TITLE_ID}>
                  {HERO_TITLE_TEXT}
                </VisuallyHidden>
                <div aria-hidden="true">
                  <HeroTitleMorph
                    sourceLines={['PAI']}
                    // "Artificial" and "Intelligence" are split into their own
                    // target lines (rather than one "Artificial Intelligence" line
                    // that relies on natural wrapping) so each word always lands on
                    // its own row at every viewport — matching the desktop layout,
                    // instead of collapsing onto one cramped line on tablet/phone.
                    targetLines={[
                      {text: 'Probability', variant: 'default'},
                      {text: 'for', variant: 'default'},
                      {text: 'Artificial', variant: 'default'},
                      {text: 'Intelligence', variant: 'default'},
                    ]}
                    startDelayMs={800}
                    onSettled={() => {
                      setTitleSettled(true);
                    }}
                  />
                </div>
                {/* Kept OUTSIDE the aria-hidden morph wrapper: when clickable,
                  the π is a real replay button and must stay in the
                  accessibility tree (a focusable control inside aria-hidden
                  would be reachable but silent). */}
                {titleSettled && copyRevealed && (
                  <PiDotPop
                    containerRef={titleRef}
                    color={HERO_PI_RED}
                    onClick={replayHero}
                    onSettled={() => {
                      setPiSettled(true);
                    }}
                  />
                )}
              </TitleStack>

              <HeroCta $animateEntrance={heroRunId === 0}>
                <HeroDeadline>First day of class: Oct 9th</HeroDeadline>
                <ApplyCardRow>
                  <ApplyCard to={PAI_STUDENT_APPLY_ROUTE}>
                    <ApplyCardTitle>Apply to learn</ApplyCardTitle>
                    <ApplyCardBody>Learn the math behind AI</ApplyCardBody>
                  </ApplyCard>
                  <ApplyCard to={PAI_SL_APPLY_ROUTE}>
                    <ApplyCardTitle>Volunteer to teach</ApplyCardTitle>
                    <ApplyCardBody>
                      Lead a small group of learners
                    </ApplyCardBody>
                  </ApplyCard>
                </ApplyCardRow>
              </HeroCta>
            </HeroContent>

            <HeroFooter
              type="button"
              $animateEntrance={heroRunId === 0}
              onClick={() => {
                scrollToCourseInfo();
              }}
            >
              <FaArrowAltCircleDown aria-hidden="true" />
              About the Class
            </HeroFooter>

            <HeroStanfordSeal alt="Stanford University seal" />
          </Hero>
        </Splash>
        {/*
        Rendered OUTSIDE <Splash> on purpose: <Splash> applies a blanket
        `* { margin: 0; padding: 0 }` reset to all descendants, which would
        collapse the Bootstrap-grid + list spacing that both the course-info
        section and the shared footer rely on. Keeping them siblings lets the
        webapp's global `style.scss` / `landing.scss` styling apply as it does
        on the main Code in Place landing page.
        */}
        <CourseInfo />
      </main>
      <SiteFooter brandName="PAI" />
    </>
  );
}
